<template>
    <div>
        <div class="flex flex-row" v-if="!object">
            <div v-if="creatable" class="relative inline-block">
                <button
                    class="border-0 bg-0 text-blue-600 flex flex-row items-center"
                    type="button"
                    @click="openNew"
                    v-if="creatable"
                >
                    + Create new entry and link
                    <span class="text-gray-400 ml-3" v-if="!model">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="3"
                                d="M19 9l-7 7-7-7"
                            />
                        </svg>
                    </span>
                </button>
                <transition name="dropdown">
                    <div
                        v-show="newOpen"
                        class="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg z-10"
                    >
                        <div
                            v-on-click-outside="() => { newOpen = false }"
                            class="rounded-md bg-white ring-1 ring-black ring-opacity-5"
                            role="menu"
                            aria-orientation="vertical"
                            aria-labelledby="options-menu"
                        >
                            <div class="py-1">
                                <a
                                    v-for="type in Object.keys(
                                        filteredContentTypes
                                    )"
                                    :key="`contentType-${type}`"
                                    @click="() => openSlide(type, 'Form')"
                                    class="cursor-pointer block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                    role="menuitem"
                                >
                                    {{ type }}
                                </a>
                            </div>
                        </div>
                    </div>
                </transition>
            </div>
            <div
                :class="['relative', 'inline-block', creatable ? 'ml-5' : null]"
                v-if="linkable"
            >
                <button
                    class="border-0 bg-0 text-blue-600 flex flex-row items-center"
                    type="button"
                    @click="openLink"
                    :dusk="`${componentName}_link_btn`"
                    v-if="linkable"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                        />
                    </svg>
                    <span class="inline-block ml-1">{{ linkText }}</span>
                    <span class="text-gray-400 ml-3" v-if="!model">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="3"
                                d="M19 9l-7 7-7-7"
                            />
                        </svg>
                    </span>
                </button>
                <transition name="dropdown">
                    <div
                        v-show="linkOpen"
                        v-on-click-outside="() => {
                            newOpen = false;
                            linkOpen = false;
                        }"
                        class="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg z-10"
                    >
                        <div
                            class="rounded-md bg-white ring-1 ring-black ring-opacity-5"
                            role="menu"
                            aria-orientation="vertical"
                            aria-labelledby="options-menu"
                        >
                            <div class="py-1">
                                <a
                                    v-for="type in Object.keys(
                                        filteredContentTypes
                                    )"
                                    :key="`contentTypeLink-${type}`"
                                    @click="() => openSlide(type, 'List')"
                                    class="cursor-pointer block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                    role="menuitem"
                                >
                                    {{ type }}
                                </a>
                            </div>
                        </div>
                    </div>
                </transition>
            </div>
        </div>
        <div v-if="object">
            <div class="flex flex-row items-center">
                <p class="text-gray-500">
                    Referenced:
                    <span class="text-gray-700">{{
                        object[
                            contentTypes[model]
                                ? contentTypes[model].columnSelector
                                : "name"
                        ] || object["name"]
                    }}</span>

                    <span v-if="debugStore.debugMode && object.id" class="font-mono">
                        [{{ model }}:{{ object.id }}]
                    </span>
                </p>
                <span
                    @click="deleteObject"
                    v-if="deletable"
                    class="text-gray-400 hover:text-gray-600 cursor-pointer ml-4 transition duration-150 transform hover:scale-105"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </span>
                <span
                    v-if="shouldOpenNewWindow"
                    class="text-gray-400 hover:text-gray-600 cursor-pointer ml-1 transition duration-150 transform hover:scale-105"
                >
                    <a :href="getHref(object)" target="_blank">
                        <svg
                            class="h-5 w-5"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                            />
                        </svg>
                    </a>
                </span>
                <span
                    @click="() => this.openSlide(this.model, 'Form')"
                    class="text-gray-400 hover:text-gray-600 cursor-pointer ml-1 transition duration-150 transform hover:scale-105"
                    v-if="editable"
                >
                    <svg
                        class="h-5 w-5"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                    </svg>
                </span>
            </div>
        </div>
        <input
            v-if="object"
            type="hidden"
            :name="inputName"
            :value="object.id"
        />
        <slide-over
            @close="closeSlide"
            :is-open="slideOpen"
            :component="component"
            :formName="baseFormName"
            :object="this.object"
            :model="model"
            :language="language"
            :excluded-ids="excludedIds"
            :routeKeyName="model ? contentTypes[model].routeKeyName : 'id'"
            :label="
                model
                    ? contentTypes[model]
                        ? contentTypes[model].columnSelector
                        : null
                    : null
            "
            :formRoute="formRoute"
            :listRoute="
                model
                    ? contentTypes[model]
                        ? contentTypes[model].listRoute
                        : null
                    : null
            "
            @created="objectCreated"
            @selected="objectSelected"
            :query="query"
            :order="order"
        />
        <div v-if="error" class="mt-3">
            <error :error="error" />
        </div>
    </div>
</template>
<script>
import reference from "../mixins/reference";
import siteLanguage from '../config/siteLanguage'
import {useDebugStore} from "../stores/debug";
export default {
    name: "PolymorphicReference",
    mixins: [reference],
    props: {
        fieldName: String,
        value: Object,
        modelData: String,
        models: Array,
        maxItems: Number,
        language: {
            type: Object,
            default: () => {
                return { id: siteLanguage.language.ENGLISH, iso: siteLanguage.iso.ENGLISH };
            },
        },
        linkText: {
            type: String,
            required: false,
            default: 'Link an existing entry',
        },
        twoStepCreate: Boolean,
        editable: {
            type: Boolean,
            default: false,
            required: false,
        },
        deletable: {
            type: Boolean,
            default: true,
            required: false,
        },
        creatable: {
            type: Boolean,
            default: true,
            required: false,
        },
        linkable: {
            type: Boolean,
            default: true,
            required: false,
        },
        inline: {
            type: Boolean,
            default: false,
            required: false,
        },
        error: {
            type: String,
            required: false,
        },
        shouldOpenNewWindow: {
            type: Boolean,
            required: false,
            default: false
        },
        excludedIds: Array,
        query: Object,
        order: Object,
    },
    data() {
        return {
            newOpen: false,
            linkOpen: false,
            slideOpen: false,
            model: this.modelData || null,
            object: this.value || null,
            component: null,
            debugStore: useDebugStore(),
        };
    },
    created() {
        if (this.object) {
            this.object = this.generateObjectUpdateRoute(this.object);
        }
        if (this.models.length === 1) {
            this.model = this.models[0];
        }
    },
    methods: {
        openSlide(model, type) {
            this.model = model;
            this.component = type === "Form" ? this.formName : "ModelList";
            this.slideOpen = true;
            this.newOpen = false;
            this.linkOpen = false;
        },
        closeSlide() {
            this.slideOpen = false;
            if (!this.object) {
                this.model = null;
            }
        },
        openLink() {
            if (this.model) {
                this.openSlide(this.model, "Link");
                return;
            }
            this.linkOpen = !this.linkOpen;
            this.newOpen = false;
        },
        openNew() {
            if (this.model) {
                this.openSlide(this.model, "Form");
                return;
            }
            this.newOpen = !this.newOpen;
            this.linkOpen = false;
        },
        objectCreated({ object }) {
            if (!this.canAddObject()) {
                this.maximumItemsReached();
                return;
            }
            object = this.generateObjectUpdateRoute(object);
            this.object = object;
            this.slideOpen = false;
        },
        objectSelected({ object }) {
            if (!this.canAddObject()) {
                this.maximumItemsReached();
                return;
            }
            object = this.generateObjectUpdateRoute(object);
            this.object = object;
            this.slideOpen = false;
        },
        canAddObject() {
            if (this.maxItems) {
                if (this.objects.length + 1 > this.maxItems) {
                    return false;
                }
            }
            return true;
        },
        maximumItemsReached() {
            this.$notify({
                group: "formError",
                title:
                    this.maxItemMessage ||
                    `You can only select ${this.maxItems} items`,
            });
        },
        deleteObject() {
            this.object = null;
            this.model = null;
        },
        getHref(object) {
            if (!this.model) {
                let modelName = object.model_name.replace('App\\Models\\','');
                this.model = modelName.match(/[A-Z][a-z]+|[0-9]+/g).join(" ");

                if (this.model == "Page") {
                   if (object.type_id == 5) {
                       this.model = "License Main Page";
                   } else if(object.type_id == 6) {
                        this.model = "Payment Method Main Page";
                   } else if(object.type_id == 7) {
                       this.model = "Knowledge Hub Page";
                   } else if(object.type_id == 8) {
                       this.model = "Blog Main Page";
                   } else if(object.type_id == 9) {
                       this.model = "Guide Main Page";
                   }
                }

                if (this.model == "Legal Page") {
                    if (object.type_id == 1) {
                        this.model = "Legal Notice Page";
                    } else if (object.type_id == 2) {
                        this.model = "Privacy Policy Page";
                    } else if (object.type_id == 3) {
                        this.model = "Cookie Policy Page";
                    } else if (object.type_id == 4) {
                        this.model = "Responsible Gambling Page";
                    }
                }
            }
            return this.generateObjectEditRoute(object).edit_route;
        }
    },
    computed: {
        formName() {
            return this.twoStepCreate && this.language.id !== 1 && !this.object
                ? `two-step-create`
                : `${this.componentName}-form`;
        },
        baseFormName() {
            return `${this.componentName}-form`;
        },
        componentName() {
            return this.model
                ? this.model.toLowerCase().split(" ").join("-")
                : "";
        },
        inputName() {
            return this.fieldName || this.model
                ? `${this.model.toLowerCase()}_id`
                : "document_id";
        },
        formRoute() {
            if (this.object) {
                return this.object.updateRoute;
            }
            if (this.model) {
                return this.contentTypes[this.model]
                    ? this.contentTypes[this.model].formRoute
                    : null;
            }
        },
        filteredContentTypes() {
            let types = {};
            this.models.forEach((model) => {
                if (this.contentTypes[model]) {
                    types[model] = this.contentTypes[model];
                }
            });
            return types;
        },
    },
    watch: {
        object(newValue) {
            this.$emit("input", newValue);
        },
        value(newValue) {
            this.object = newValue;
        },
    },
};
</script>
